// « Studio » (docs/wip/SPEC-STUDIO.md §3): the creations a department follows, in one full-screen
// sheet that keeps its place while one navigates:
// - the list (#/etage/<n>/studio: every studio service of the floor; …/studio/<deptId>: one
//   service) — type tabs, counters per status (clickable), filters (service, agent, tag, search,
//   sort) and a grid of cards (thumbnail, title, coloured status — for an ad on brief, where its
//   script stands —, children counts, date); « Nouveau » (« Nouvelle personne ») creates an element
//   of a tab without parent type, « Nouvelle pub » (studio on brief) a brief and its pub together,
//   « Ajouter des hooks » (Hooks tab, portrait cards) one hook per video sent (studio/hooks.js);
// - an element (#/studio/item/<id>) — a pub opens its own page (studio-pub.js), a brief its pub's
//   page (« Créer la pub » on its sheet when it has none); else breadcrumb up its parents, header (status, agent, links),
//   fields (long texts folded), files (image preview, video player), children grouped by type,
//   history, and « Regénérer », « Changer le statut », « Supprimer » (admin); with operate, its
//   title and fields are editable (« Modifier ») and files are added (with a role) or removed;
// - its tree (#/studio/item/<id>/arbre) — one row per scene, one column per type below it, the
//   elements still pending in dashed outline.
// Live: every `studio` event of the floor reloads what is shown (debounced). Text written by
// agents is always shown as text, never as markup.
// An element of another floor (a parent or a child of this floor's: agents link the elements of
// the floors they reach) names its floor on its service's badge (« Catalogue · Marketing »).
import {
  h, button, icon, select, input, textarea, field, tabs, emptyState, spinner, setText, classNames,
} from '../dom.js';
import { t } from '../i18n/fr.js';
import { relativeTime, formatDateTime, deptColor } from '../format.js';
import { floorNumberOf, studioHash, routeHash, floorByNumber } from '../store.js';
import {
  studioDepts, studioDept, kindTabs, defaultKind, typeOf, statusInfo, statusChips, listParams, childrenLine,
  crumbChain, groupByKind, treeRows, tagsOf, eventMatches, creatableDepts, adDepts, fileRoles, refLabel, SORTS, PAGE_SIZE, AD_FIELDS,
} from './studio/model.js';
import { briefPhase } from './studio/pub-model.js';
import { fieldRows, editForm, filesPanel, historyList, uploadFiles, UPLOAD_ACCEPT, liveReloadWaits } from './studio/item-parts.js';
import { openAddHooks } from './studio/hooks.js';
import { copyText } from './tasks/components.js';
import { createPubView } from './studio-pub.js';
import './studio/strings.js';

/** The kinds shown inside their ad's page rather than on a sheet of their own. */
const STEP_IN_PUB = new Set(['scene', 'image', 'sequence']);
/** The tabs whose cards are portrait (vertical hook videos, 9:16). */
const PORTRAIT_KINDS = new Set(['hook']);
/** Live events in a row give one reload. */
const RELOAD_DEBOUNCE_MS = 400;
/** Typing in the search box waits this long before asking the server. */
const SEARCH_DEBOUNCE_MS = 250;

const enc = encodeURIComponent;
/** The filters of each list (floor, service), kept while the sheet is open or reopened. */
const remembered = new Map();

function itemHash(id, tree = false) {
  return routeHash({ name: 'studioItem', id, tab: tree ? 'arbre' : null });
}

/**
 * Where the script of an ad on brief stands, as a chip: « Script à valider »… (the brief's own
 * status label when the phase has no sentence), with the colour of the brief's status.
 */
function phaseInfo(briefType, phase) {
  const info = statusInfo(briefType, phase);
  const key = `studio.brief.phase.${phase}`;
  const label = t(key);
  return { ...info, label: label === key ? info.label : label };
}

/** A coloured status chip. */
function statusChip(info, opts = {}) {
  return h('span', { class: classNames(['st-status', `st-tone-${info.tone}`, opts.class]), title: opts.title || null },
    h('span', { class: 'st-status-dot', 'aria-hidden': 'true' }), info.label);
}

/**
 * The short reference of an element (« #K7Q2 »), the code the owner gives the agents: a plain
 * chip inside a link (cards), a copy button in the element's header (`ctx`). Null without a ref.
 */
function refChip(ref, ctx = null) {
  const label = refLabel(ref);
  if (!label) return null;
  if (!ctx) return h('span', { class: 'st-ref', title: t('studio.ref.title') }, label);
  return h('button', {
    type: 'button', class: 'st-ref st-ref-copy', title: t('studio.ref.copy'), 'aria-label': t('studio.ref.copyLabel', { ref: label }),
    onClick: () => copyText(ctx, label),
  }, label);
}

/**
 * The small badge of a service (its colour and name); with `floor`, the name of its floor too (an
 * element of another floor: « Catalogue · Marketing »).
 */
function deptBadge(dept, name, floor = null) {
  const label = dept?.name || name || '';
  return h('span', {
    class: 'st-dept', style: { '--dept': deptColor(dept?.color, dept?.id || name) },
    title: floor ? t('studio.deptOnFloorTitle', { dept: label, floor }) : null,
  },
  h('span', { class: 'st-dept-dot', 'aria-hidden': 'true' }), floor ? t('studio.deptOnFloor', { dept: label, floor }) : label);
}

/** The name of a floor (its id when unknown). */
function floorName(office, floorId) {
  return (office?.floors || []).find((f) => f.id === floorId)?.name || floorId || '';
}

/** A thumbnail: the element's first image (or video), else its type's initial. */
function thumbOf(summary, label) {
  const th = summary.thumb;
  if (th?.url && th.media === 'image') return h('img', { class: 'st-thumb-media', src: th.url, alt: '', loading: 'lazy', decoding: 'async' });
  if (th?.url && th.media === 'video') {
    return h('span', { class: 'st-thumb-video' },
      h('video', { class: 'st-thumb-media', src: `${th.url}#t=0.5`, muted: true, preload: 'metadata', playsinline: true, 'aria-hidden': 'true' }),
      h('span', { class: 'st-thumb-play', 'aria-hidden': 'true' }, icon('film', { size: 14 })));
  }
  return h('span', { class: 'st-thumb-empty', 'aria-hidden': 'true' }, h('span', { class: 'st-thumb-letter' }, String(label || '?').slice(0, 1)));
}

/**
 * Opens the Studio sheet on a route ({name: 'studio', number, id?} or {name: 'studioItem', id,
 * tab?}); the returned layer has show(route) to move inside it without closing it.
 * @param {object} ctx app context
 * @param {object} route
 * @returns {import('./overlay.js').Layer & {show(route: object): void}}
 */
export function openStudio(ctx, route) {
  const shell = h('div', { class: 'st-shell' });
  const layer = ctx.overlay.openSheet({ title: t('studio.title'), eyebrow: t('studio.title').toUpperCase(), content: shell, className: 'st-sheet' });
  let view = null;

  function show(r) {
    // Inside an ad's page (the ad ↔ one of its elements): the same page shows it, no reload.
    if (view?.tryShow?.(r)) return;
    try { view?.destroy(); } catch (err) { console.error(err); }
    view = r.name === 'studioItem' ? createItemView(ctx, r, layer) : createListView(ctx, r, layer);
    shell.replaceChildren(view.el);
    layer.body?.scrollTo?.(0, 0);
  }

  const offs = [
    ctx.events.on('studio', (ev) => view?.onEvent?.(ev)),
    ctx.events.on('hello', () => view?.reload?.()),
    ctx.store.on('office', () => view?.officeChanged?.()),
  ];
  layer.onClose(() => {
    for (const off of offs) off();
    try { view?.destroy(); } catch (err) { console.error(err); }
    view = null;
  });
  layer.show = show;
  show(route);
  return layer;
}

// ---------------------------------------------------------------------------------------------
// List

/**
 * The list of a floor's studios (or one service's).
 * @param {object} ctx
 * @param {{number: number, id?: string|null}} route
 * @param {object} layer
 */
export function createListView(ctx, route, layer) {
  const { store } = ctx;
  const timers = new Set();
  const later = (fn, ms) => { const id = setTimeout(() => { timers.delete(id); fn(); }, ms); timers.add(id); return id; };
  let destroyed = false;
  let seq = 0;
  let res = null;

  const floor = floorByNumber(store.get().office.floors, route.number);
  const floorId = floor?.id || null;
  const key = `${floorId}|${route.id || ''}`;
  const st = {
    deptId: route.id || null, kind: null, status: null, agentId: '', tag: '', search: '', sort: 'recent', limit: PAGE_SIZE,
    ...(remembered.get(key) || {}),
  };
  const el = h('section', { class: 'st-view st-list', 'aria-label': t('studio.title') });

  const depts = () => studioDepts(store.get().office, floorId);
  const shown = () => (st.deptId ? depts().filter((d) => d.id === st.deptId) : depts());
  const deptById = (id) => studioDept(store.get().office, id);
  const isAdmin = () => store.get().me?.role === 'admin';
  const deptName = st.deptId ? store.get().office.departments.find((d) => d.id === st.deptId)?.name : null;

  layer.setTitle?.(st.deptId ? t('studio.deptTitle', { dept: deptName || st.deptId }) : t('studio.floorTitle'));
  layer.setEyebrow?.(t('studio.eyebrow', { floor: (floor?.name || '').toUpperCase() }));

  // --- skeleton ------------------------------------------------------------------------------
  const deptSelect = select([], { class: 'st-dept-select', 'aria-label': t('studio.deptLabel') });
  deptSelect.addEventListener('change', () => ctx.navigate(studioHash(store.get().office.floors, floorId, deptSelect.value || null)));
  const newBtn = button(t('studio.new'), { variant: 'primary', size: 'sm', icon: 'plus', class: 'st-new', onClick: () => openCreate() });
  const count = h('span', { class: 'st-count mono-label', 'aria-live': 'polite' });
  const live = h('span', { class: 'st-live mono-label' }, h('span', { class: 'live-dot', 'aria-hidden': 'true' }), t('studio.live'));
  const head = h('div', { class: 'st-toolbar' },
    h('div', { class: 'st-toolbar-left' }, h('label', { class: 'st-select-wrap' }, icon('layers', { size: 15 }), deptSelect), count),
    h('div', { class: 'st-toolbar-right' }, live, newBtn));
  const tabsHost = h('div', { class: 'st-tabs' });
  const chipsHost = h('div', { class: 'st-chips', role: 'group', 'aria-label': t('studio.statusesLabel') });
  const searchBox = input({ type: 'search', class: 'st-search', placeholder: t('studio.search'), 'aria-label': t('common.search'), value: st.search });
  const agentSelect = select([], { class: 'st-filter', 'aria-label': t('studio.agent') });
  const tagSelect = select([], { class: 'st-filter', 'aria-label': t('studio.tag') });
  const sortSelect = select(SORTS.map((s) => ({ value: s, label: t(`studio.sort.${s}`) })), { class: 'st-filter', value: st.sort, 'aria-label': t('studio.sort') });
  const filters = h('div', { class: 'st-filters' },
    h('label', { class: 'st-search-wrap' }, icon('search', { size: 15 }), searchBox), agentSelect, tagSelect, sortSelect);
  const grid = h('div', { class: 'st-grid', role: 'list' });
  const foot = h('div', { class: 'st-foot' });
  el.append(head, tabsHost, chipsHost, filters, grid, foot);

  // Its own timer: clearing every timer would also drop a live reload waiting (and stop them all).
  let searchTimer = null;
  searchBox.addEventListener('input', () => {
    clearTimeout(searchTimer);
    timers.delete(searchTimer);
    searchTimer = later(() => { searchTimer = null; st.search = searchBox.value; st.limit = PAGE_SIZE; load(); }, SEARCH_DEBOUNCE_MS);
  });
  agentSelect.addEventListener('change', () => { st.agentId = agentSelect.value; st.limit = PAGE_SIZE; load(); });
  tagSelect.addEventListener('change', () => { st.tag = tagSelect.value; st.limit = PAGE_SIZE; load(); });
  sortSelect.addEventListener('change', () => { st.sort = sortSelect.value; load(); });

  function remember() {
    const { deptId, ...rest } = st;
    remembered.set(key, { ...rest, limit: PAGE_SIZE });
  }

  // --- rendering ------------------------------------------------------------------------------
  function renderHead() {
    const list = depts();
    deptSelect.replaceChildren(h('option', { value: '' }, t('studio.allDepts')), ...list.map((d) => h('option', { value: d.id }, d.name)));
    deptSelect.value = st.deptId || '';
    const tabList = kindTabs(shown());
    if (!st.kind || !tabList.some((x) => x.kind === st.kind)) st.kind = defaultKind(tabList);
    const bar = tabs(tabList.map((x) => ({ id: x.kind, label: x.plural, count: res?.kinds?.[x.kind] ?? null })), {
      active: st.kind, label: t('studio.kindsLabel'),
      onChange: (kind) => { st.kind = kind; st.status = null; st.limit = PAGE_SIZE; load(); },
    });
    bar.classList.add('st-kind-tabs');
    tabsHost.replaceChildren(bar);
    const where = creatable();
    newBtn.hidden = !where.length;
    // On « Hooks »: « Ajouter des hooks » (an upload: its icon), not « Nouveau ».
    const newIcon = st.kind === 'hook' ? 'upload' : 'plus';
    if (newBtn.dataset.icon !== newIcon) {
      newBtn.querySelector('.icon')?.remove();
      newBtn.insertBefore(icon(newIcon, { size: 16 }), newBtn.firstChild);
      newBtn.dataset.icon = newIcon;
    }
    setText(newBtn.querySelector('.btn-label') || newBtn, st.kind === 'person' ? t('studio.new.person') : st.kind === 'pub' ? t('studio.new.pub')
      : st.kind === 'hook' ? t('studio.hooks.add') : t('studio.new'));
    const agents = store.get().office.agents.filter((a) => shown().some((d) => d.id === a.deptId));
    agentSelect.replaceChildren(h('option', { value: '' }, t('studio.allAgents')), ...agents.map((a) => h('option', { value: a.id }, a.name)));
    agentSelect.value = st.agentId || '';
    agentSelect.hidden = agents.length < 1;
  }

  function renderChips() {
    const chips = statusChips(shown(), st.kind, res?.counts || {});
    const total = chips.reduce((n, c) => n + c.count, 0);
    const chip = (label, n, active, tone, onClick) => h('button', {
      type: 'button', class: classNames(['st-chip', tone && `st-tone-${tone}`, { 'is-active': active, 'is-zero': !n }]), 'aria-pressed': String(active), onClick,
    }, tone ? h('span', { class: 'st-status-dot', 'aria-hidden': 'true' }) : null, h('span', { class: 'st-chip-label' }, label), h('span', { class: 'st-chip-count' }, String(n)));
    chipsHost.replaceChildren(
      chip(t('studio.allStatuses'), total, !st.status, null, () => { st.status = null; load(); }),
      ...chips.map((c) => chip(c.label, c.count, st.status === c.key, c.tone, () => { st.status = st.status === c.key ? null : c.key; load(); })),
    );
  }

  function card(it) {
    const dept = deptById(it.deptId);
    const type = typeOf(dept, it.kind);
    const tabList = kindTabs(depts(), { all: true });
    const kids = childrenLine(it.childCounts, tabList);
    const info = statusInfo(type, it.status);
    // An ad on brief whose script is not approved yet: where the script stands, not « En production ».
    const fromBrief = it.kind === 'pub' && it.origin?.kind === 'brief';
    const briefType = fromBrief ? typeOf(dept, 'brief') : null;
    const phase = fromBrief ? briefPhase(it.origin, briefType) : null;
    const chip = phase ? statusChip(phaseInfo(briefType, phase), { class: 'st-phase', title: t('studio.brief.phaseTitle') }) : statusChip(info);
    return h('a', {
      class: classNames(['st-card', { 'is-pending': info.pending, 'is-error': info.error }]), href: itemHash(it.id), role: 'listitem',
      dataset: { id: it.id, status: it.status }, style: { '--dept': deptColor(dept?.color, it.deptId) },
    },
    h('span', { class: 'st-thumb' }, it.kind === 'pub' && !it.thumb
      ? h('span', { class: 'st-thumb-empty st-thumb-novideo' }, icon('film', { size: 18 }), h('span', { class: 'mono-label' }, t('studio.pub.noVideoShort')))
      : thumbOf(it, type?.label || it.kind), it.lock ? h('span', { class: 'st-lock', title: t('studio.locked') }, icon('clock', { size: 12 })) : null),
    h('span', { class: 'st-card-body' },
      h('span', { class: 'st-card-title' }, it.title),
      h('span', { class: 'st-card-row' }, refChip(it.ref), chip, st.deptId ? null : deptBadge(dept, it.deptName)),
      kids ? h('span', { class: 'st-card-kids' }, kids) : null,
      h('span', { class: 'st-card-date mono-label', title: formatDateTime(it.updatedAt) }, relativeTime(it.updatedAt))));
  }

  function renderItems() {
    const items = res?.items || [];
    setText(count, t('studio.count', { n: res?.total ?? 0 }));
    const tags = [...new Set([...tagsOf(items), ...(st.tag ? [st.tag] : [])])];
    tagSelect.replaceChildren(h('option', { value: '' }, t('studio.allTags')), ...tags.map((x) => h('option', { value: x }, x)));
    tagSelect.value = st.tag || '';
    tagSelect.hidden = !tags.length;
    grid.classList.toggle('is-portrait', PORTRAIT_KINDS.has(st.kind));
    if (!items.length) {
      const filtered = Boolean(st.status || st.agentId || st.tag || st.search);
      grid.replaceChildren(emptyState({
        icon: filtered ? 'search' : 'film',
        title: filtered ? t('studio.empty.filtered') : t('studio.empty.title'),
        text: filtered ? null : t('studio.empty.text'),
        action: filtered ? button(t('studio.clearFilters'), {
          variant: 'secondary', size: 'sm',
          onClick: () => { Object.assign(st, { status: null, agentId: '', tag: '', search: '' }); searchBox.value = ''; load(); },
        }) : null,
      }));
    } else {
      grid.replaceChildren(...items.map(card));
    }
    foot.replaceChildren();
    if (res && res.total > items.length) {
      foot.append(button(t('studio.more', { n: items.length, total: res.total }), {
        variant: 'secondary', onClick: () => { st.limit = Math.min(500, st.limit + PAGE_SIZE); load(); },
      }));
    }
  }

  /**
   * The services where the current tab's elements can be created from here; on « Nos pubs », those
   * whose pubs come from a brief (« Nouvelle pub »); on « Hooks », those with a hook type.
   */
  function creatable() {
    const operate = (d) => ctx.can('operate', { floorId: d.floorId || floorId, deptId: d.id });
    return st.kind === 'pub' ? adDepts(shown(), operate) : creatableDepts(shown(), st.kind, operate);
  }

  /**
   * « Nouveau » / « Nouvelle personne »: a title (a name), the service when several fit;
   * « Ajouter des hooks »: the hook videos (the list reloads once they are sent).
   */
  function openCreate() {
    const where = creatable();
    if (!where.length) return null;
    if (st.kind === 'pub') return openCreateAd(where);
    if (st.kind === 'hook') return openAddHooks(ctx, where, { onDone: () => { if (!destroyed) load(); } });
    const person = st.kind === 'person';
    const kindLabel = typeOf(where[0], st.kind)?.label || st.kind;
    const title = input({ maxLength: 200, class: 'st-new-title', required: true });
    // Preselected: the first service set up with its own types (studioDepts puts them first).
    const deptSel = select(where.map((d) => ({ value: d.id, label: d.name })), { class: 'st-new-dept', value: where[0].id });
    const titleField = field(person ? t('studio.new.namePerson') : t('studio.new.name'), title);
    const form = h('form', { class: 'st-new-form' }, titleField, where.length > 1 ? field(t('studio.new.dept'), deptSel) : null);
    const submit = button(t('studio.new.create'), { variant: 'primary', class: 'st-new-create' });
    const create = async (e) => {
      e?.preventDefault?.();
      const value = String(title.value || '').trim();
      if (!value) { titleField.setError(t('studio.new.required')); return; }
      submit.disabled = true;
      try {
        const deptId = (where.length > 1 && deptSel.value) || where[0].id;
        const created = await ctx.api.post(`/api/studio/depts/${enc(deptId)}/items`, { kind: st.kind, title: value });
        modal.close('saved');
        ctx.toast.success(t('studio.new.done'), { timeout: 3000 });
        ctx.navigate(itemHash(created.id));
      } catch (err) {
        ctx.toast.error(err);
      } finally {
        submit.disabled = false;
      }
    };
    form.addEventListener('submit', create);
    submit.addEventListener('click', create);
    const modal = ctx.overlay.openModal({
      title: person ? t('studio.new.titlePerson') : t('studio.new.title', { kind: kindLabel }), size: 'sm', content: form,
      footer: [button(t('common.cancel'), { variant: 'ghost', onClick: () => modal.close('cancel') }), submit], initialFocus: title,
    });
    return modal;
  }

  /**
   * « Nouvelle pub »: its title, the brief's fields the owner fills (the idea, required, then
   * product, audience, message, references, as the service's brief type has them), the service
   * when several fit and reference files. POST …/ads creates the brief and its pub together; the
   * files go to the brief (role « reference »); then the pub's page opens.
   */
  function openCreateAd(where) {
    const title = input({ maxLength: 200, class: 'st-new-title', required: true });
    const titleField = field(t('studio.new.name'), title);
    // Preselected: the first service set up with its own types (« Création Ads San », not a
    // « Direction » on the default ones, whose briefs no agent would write).
    const deptSel = select(where.map((d) => ({ value: d.id, label: d.name })), { class: 'st-new-dept', value: where[0].id });
    const fieldsHost = h('div', { class: 'st-new-fields' });
    const picker = h('input', { type: 'file', multiple: true, accept: UPLOAD_ACCEPT, class: 'st-new-files' });
    const picked = h('p', { class: 'field-hint st-new-picked' });
    picker.addEventListener('change', () => setText(picked, picker.files?.length ? t('studio.newAd.picked', { n: picker.files.length }) : ''));
    let controls = [];
    const deptOf = () => where.find((d) => d.id === deptSel.value) || where[0];
    const renderFields = () => {
      const type = typeOf(deptOf(), 'brief');
      const before = new Map(controls.map((c) => [c.key, c.control.value]));
      controls = AD_FIELDS.map((key) => (type?.fields || []).find((f) => f.key === key)).filter(Boolean).map((def) => {
        const props = { class: 'st-new-field', dataset: { key: def.key }, ...(def.type === 'longtext' ? {} : { maxLength: 4000 }) };
        const control = def.type === 'longtext' ? textarea({ ...props, rows: def.key === 'brief' ? 5 : 3 }) : input(props);
        control.value = before.get(def.key) || '';
        const required = def.key === 'brief';
        const wrap = field(def.label, control, { optional: !required, hint: required ? t('studio.newAd.ideaHint') : null });
        return { key: def.key, def, control, wrap, required };
      });
      fieldsHost.replaceChildren(...controls.map((c) => c.wrap));
    };
    deptSel.addEventListener('change', renderFields);
    renderFields();
    const form = h('form', { class: 'st-new-form' },
      titleField,
      where.length > 1 ? field(t('studio.new.dept'), deptSel) : null,
      fieldsHost,
      field(t('studio.newAd.files'), picker, { optional: true, hint: t('studio.newAd.filesHint') }), picked);
    const submit = button(t('studio.newAd.create'), { variant: 'primary', class: 'st-new-create' });
    const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => modal.close('cancel') });
    // One ad per « Créer la pub »: a second click or Enter while it is being created (or while its
    // files are sent) does nothing; once created, the ad is never created again.
    let busy = false;
    const create = async (e) => {
      e?.preventDefault?.();
      if (busy) return;
      const value = String(title.value || '').trim();
      let bad = false;
      titleField.setError(value ? null : t('studio.new.required'));
      if (!value) bad = true;
      const data = {};
      for (const c of controls) {
        const v = c.def.type === 'longtext' ? c.control.value : c.control.value.trim();
        if (v.trim()) data[c.key] = v;
        c.wrap.setError(c.required && !v.trim() ? t('studio.newAd.ideaRequired') : null);
        if (c.required && !v.trim()) bad = true;
      }
      if (bad) return;
      busy = true;
      submit.disabled = true;
      cancel.disabled = true;
      const files = [...(picker.files || [])];
      let out;
      try {
        out = await ctx.api.post(`/api/studio/depts/${enc(deptOf().id)}/ads`, { title: value, data });
      } catch (err) {
        ctx.toast.error(err);
        busy = false;
        submit.disabled = false;
        cancel.disabled = false;
        return;
      }
      try {
        // The files go to the brief (« Envoi 1/2… » on the button); a refused file has its own
        // toast and the ad stays created.
        if (files.length) await uploadFiles(ctx, out.brief.id, files, 'reference', { btn: submit, idleLabel: t('studio.newAd.create') });
      } catch (err) {
        ctx.toast.error(err);
      } finally {
        modal.close('saved');
        ctx.toast.success(t('studio.newAd.done'), { timeout: 3000 });
        ctx.navigate(itemHash(out.pub.id));
      }
    };
    form.addEventListener('submit', create);
    submit.addEventListener('click', create);
    const modal = ctx.overlay.openModal({
      title: t('studio.newAd.title'), size: 'md', content: form,
      footer: [cancel, submit], initialFocus: title,
    });
    return modal;
  }

  /** No studio to show: the floor's is off (an administrator switches it on in Étages & services). */
  function renderNone() {
    el.replaceChildren(head, emptyState({
      icon: 'film',
      title: t('studio.none.title'),
      text: t('studio.none.text'),
      action: isAdmin() ? button(t('studio.none.admin'), { variant: 'primary', onClick: () => ctx.navigate('#/admin/floors') }) : null,
    }));
    renderHead();
  }

  // --- data -----------------------------------------------------------------------------------
  /** The reload the live events wait for (reloadSoon). */
  let pending = null;
  async function load() {
    remember();
    // This load covers the live events waiting for their reload.
    if (pending) { clearTimeout(pending); timers.delete(pending); pending = null; }
    if (!floorId) return;
    if (!shown().length) { renderNone(); return; }
    if (!el.contains(grid)) el.replaceChildren(head, tabsHost, chipsHost, filters, grid, foot);
    renderHead();
    const mine = ++seq;
    el.classList.add('is-loading');
    if (!res) grid.replaceChildren(h('div', { class: 'st-loading' }, spinner()));
    try {
      const out = await ctx.api.get(ctx.api.withQuery(`/api/studio/floors/${enc(floorId)}/items`, listParams(st)));
      if (destroyed || mine !== seq) return;
      res = out;
      renderHead();
      renderChips();
      renderItems();
    } catch (err) {
      if (destroyed || mine !== seq) return;
      grid.replaceChildren(emptyState({
        icon: 'alert', title: t('studio.loadError'), text: err?.message || '',
        action: button(t('studio.retry'), { variant: 'secondary', size: 'sm', onClick: () => load() }),
      }));
    } finally {
      if (mine === seq) el.classList.remove('is-loading');
    }
  }

  function onEvent(ev) {
    if (!eventMatches(ev, { floorId, deptId: st.deptId })) return;
    reloadSoon();
  }

  /** One reload for the live events in a row; while files are being sent (« Ajouter des hooks »: one event per hook), once they are done. */
  function reloadSoon() {
    if (pending) return;
    pending = later(() => {
      pending = null;
      if (liveReloadWaits(el)) reloadSoon();
      else load();
    }, RELOAD_DEBOUNCE_MS);
  }

  if (!floorId) {
    el.replaceChildren(emptyState({ icon: 'alert', title: t('app.unknownFloor') }));
  } else {
    load();
  }

  return {
    el,
    onEvent,
    reload: () => load(),
    officeChanged: () => load(),
    /** Test hook: the state of the filters. */
    state: st,
    destroy() {
      destroyed = true;
      for (const id of timers) clearTimeout(id);
      timers.clear();
    },
  };
}

// ---------------------------------------------------------------------------------------------
// Element

/**
 * One element: detail (#/studio/item/<id>) or tree (…/arbre).
 * @param {object} ctx
 * @param {{id: string, tab?: string|null}} route
 * @param {object} layer
 */
export function createItemView(ctx, route, layer) {
  const { store } = ctx;
  const timers = new Set();
  let destroyed = false;
  let seq = 0;
  let item = null;
  let tree = null;
  /** The fields shown as a form (« Modifier »). */
  let editing = false;
  const treeMode = route.tab === 'arbre';
  /** Our ads open on their own page (studio-pub.js), unless the raw sheet (« /fiche ») is asked. */
  let pubView = null;
  const el = h('article', { class: classNames(['st-view', 'st-item', { 'is-tree': treeMode }]) }, h('div', { class: 'st-loading' }, spinner()));

  const deptById = (id) => studioDept(store.get().office, id);
  const agentById = (id) => store.get().office.agents.find((a) => a.id === id) || null;
  const floors = () => store.get().office.floors;
  const canOperate = () => Boolean(item) && ctx.can('operate', { floorId: item.floorId, deptId: item.deptId });
  const isAdmin = () => store.get().me?.role === 'admin';

  function infoOf(s) {
    return statusInfo(typeOf(deptById(s.deptId), s.kind), s.status);
  }

  /** A small clickable card of another element (parent, child, tree cell). */
  function mini(s, { showDept = false, pendingOutline = false } = {}) {
    const dept = deptById(s.deptId);
    const type = typeOf(dept, s.kind);
    const info = statusInfo(type, s.status);
    const cross = item && s.deptId !== item.deptId;
    // An element of another floor: its badge names that floor.
    const elsewhere = item && s.floorId && s.floorId !== item.floorId ? floorName(store.get().office, s.floorId) : null;
    return h('a', {
      class: classNames(['st-mini', { 'is-pending': pendingOutline && info.pending, 'is-error': info.error, 'is-cross': cross }]),
      href: itemHash(s.id), dataset: { id: s.id }, title: s.title, style: { '--dept': deptColor(dept?.color, s.deptId) },
    },
    h('span', { class: 'st-mini-thumb' }, thumbOf(s, type?.label || s.kind)),
    h('span', { class: 'st-mini-body' },
      h('span', { class: 'st-mini-kind mono-label' }, type?.label || s.kind, s.ref ? h('span', { class: 'st-mini-ref' }, refLabel(s.ref)) : null),
      h('span', { class: 'st-mini-title' }, s.title),
      h('span', { class: 'st-mini-row' }, statusChip(info), showDept || cross || elsewhere ? deptBadge(dept, s.deptName, elsewhere) : null)));
  }

  function crumbs() {
    const known = new Map();
    const collect = (n) => { for (const p of n?.up || []) { known.set(p.id, p); collect(p); } };
    collect(tree?.tree);
    const withUp = item.parents.map((p) => known.get(p.id) || p);
    const chain = crumbChain({ ...item, parents: withUp }, known);
    const dept = deptById(item.deptId);
    const listHash = studioHash(floors(), item.floorId, null);
    const parts = [
      h('a', { class: 'st-crumb st-crumb-root', href: listHash }, icon('film', { size: 14 }), t('studio.title')),
      dept ? h('a', { class: 'st-crumb', href: studioHash(floors(), item.floorId, dept.id) }, dept.name) : null,
      ...chain.map((c) => h('a', { class: 'st-crumb', href: itemHash(c.id), title: c.title },
        h('span', { class: 'st-crumb-kind' }, typeOf(deptById(c.deptId), c.kind)?.label || c.kind), h('span', { class: 'st-crumb-title' }, c.title))),
      h('span', { class: 'st-crumb is-current', 'aria-current': 'page' }, item.title),
    ].filter(Boolean);
    const nav = h('nav', { class: 'st-crumbs', 'aria-label': t('studio.crumbsLabel') });
    // On a phone only the service, the direct parent and the element stay: « … » stands for the rest.
    const first = dept ? 2 : 1;
    const mid = (i) => i >= first && i < parts.length - 2;
    const sep = () => h('span', { class: 'st-crumb-sep', 'aria-hidden': 'true' }, icon('chevronRight', { size: 13 }));
    parts.forEach((p, i) => {
      if (i === first && mid(i)) nav.append(h('span', { class: 'st-crumb-group st-crumb-more', 'aria-hidden': 'true' }, sep(), h('span', { class: 'st-crumb' }, '…')));
      nav.append(h('span', { class: classNames(['st-crumb-group', { 'is-mid': mid(i) }]) }, i ? sep() : null, p));
    });
    return nav;
  }

  function header() {
    const dept = deptById(item.deptId);
    const type = typeOf(dept, item.kind);
    const info = statusInfo(type, item.status);
    let status;
    if (canOperate() && type) {
      const sel = select(type.statuses.map((s) => ({ value: s.key, label: s.label })), { class: classNames(['st-status-select', `st-tone-${info.tone}`]), value: item.status, 'aria-label': t('studio.status') });
      sel.addEventListener('change', () => changeStatus(sel.value, sel));
      status = h('label', { class: 'st-status-edit' }, h('span', { class: 'st-status-dot', 'aria-hidden': 'true' }), sel);
      status.classList.add(`st-tone-${info.tone}`);
    } else {
      status = statusChip(info);
    }
    const agent = agentById(item.agentId || type?.agentId) || null;
    const lead = agentById(dept?.leadAgentId);
    const makeBtn = canMakePub() ? button(t('studio.brief.makePub'), { variant: 'primary', size: 'sm', icon: 'film', class: 'st-make-pub' }) : null;
    makeBtn?.addEventListener('click', () => makePub(makeBtn));
    const actions = h('div', { class: 'st-actions' },
      h('div', { class: 'segmented segmented-sm st-viewswitch', role: 'group', 'aria-label': t('studio.viewLabel') },
        h('a', { class: 'segmented-opt', href: itemHash(item.id), 'aria-current': treeMode ? null : 'page' }, icon('list', { size: 14 }), h('span', {}, t('studio.view.detail'))),
        h('a', { class: 'segmented-opt', href: itemHash(item.id, true), 'aria-current': treeMode ? 'page' : null }, icon('layers', { size: 14 }), h('span', {}, t('studio.view.tree')))),
      makeBtn,
      canOperate() ? button(t('studio.regen'), { variant: makeBtn ? 'secondary' : 'primary', size: 'sm', icon: 'refresh', onClick: () => regenerate(agent || lead), class: 'st-regen' }) : null,
      isAdmin() ? button(t('studio.delete'), { variant: 'ghost', size: 'sm', onClick: () => remove(), class: 'st-delete' }) : null);
    return h('header', { class: 'st-item-head' },
      h('div', { class: 'st-item-thumb' }, thumbOf({ thumb: firstMedia() }, type?.label || item.kind)),
      h('div', { class: 'st-item-titles' },
        h('p', { class: 'st-item-kind mono-label' }, type?.label || item.kind),
        h('div', { class: 'st-item-titleline' }, h('h3', { class: 'st-item-title' }, item.title), refChip(item.ref, ctx)),
        h('div', { class: 'st-item-meta' },
          status,
          deptBadge(dept, item.deptName),
          h('span', { class: 'st-meta', title: t('studio.responsible') }, icon('user', { size: 14 }), agent?.name || lead?.name || t('studio.noAgent')),
          item.externalUrl ? h('a', { class: 'st-meta st-ext', href: item.externalUrl, target: '_blank', rel: 'noopener noreferrer' }, icon('arrowRight', { size: 14 }), t('studio.external')) : null,
          h('span', { class: 'st-meta mono-label', title: formatDateTime(item.updatedAt) }, t('studio.updatedAgo', { when: relativeTime(item.updatedAt) })))),
      actions);
  }

  /** Every type of the floor's studios in pipeline order (the item's own service first). */
  function pipelineOrder() {
    const depts = studioDepts(store.get().office, item.floorId);
    const own = depts.filter((d) => d.id === item.deptId);
    const out = [];
    for (const d of [...own, ...depts.filter((x) => x.id !== item.deptId)]) {
      for (const tp of d.studio.pipeline) if (!out.some((x) => x.kind === tp.kind)) out.push({ kind: tp.kind });
    }
    return out;
  }

  function firstMedia() {
    const f = item.files.find((x) => x.media === 'image' && x.role === 'base') || item.files.find((x) => x.media === 'image')
      || item.files.find((x) => x.media === 'video');
    return f ? { url: f.url, media: f.media, name: f.name } : null;
  }

  function fieldsBlock() {
    const type = typeOf(deptById(item.deptId), item.kind);
    if (editing) {
      const form = editForm(ctx, item, type, {
        onSaved: (updated) => { item = updated; editing = false; render(); },
        onCancel: () => { editing = false; render(); },
      });
      return section(t('studio.fields'), form);
    }
    const rows = fieldRows(item, type);
    if (item.tags.length) {
      rows.push(h('div', { class: 'st-field' }, h('dt', { class: 'st-field-label mono-label' }, t('studio.tags')),
        h('dd', { class: 'st-field-value st-tags' }, ...item.tags.map((x) => h('span', { class: 'st-tag' }, x)))));
    }
    const edit = canOperate() && type ? button(t('studio.edit'), { variant: 'ghost', size: 'sm', icon: 'pencil', class: 'st-edit', onClick: () => { editing = true; render(); } }) : null;
    return section(t('studio.fields'), rows.length ? h('dl', { class: 'st-fields' }, ...rows) : h('p', { class: 'st-muted' }, t('studio.noFields')), null, edit);
  }

  function filesBlock() {
    const body = filesPanel(ctx, item, { operate: canOperate(), roles: fileRoles(item.kind), onChanged: () => load(), isDestroyed: () => destroyed });
    return section(t('studio.files'), body, item.files.length || null);
  }

  function relationsBlock() {
    const tabList = kindTabs(studioDepts(store.get().office, item.floorId), { all: true });
    const out = [];
    if (item.parents.length) out.push(section(t('studio.parents'), h('div', { class: 'st-minis' }, ...item.parents.map((p) => mini(p))), item.parents.length));
    const groups = groupByKind(item.children, tabList);
    const body = groups.length ? groups.map((g) => {
      const tab = tabList.find((x) => x.kind === g.kind);
      return h('div', { class: 'st-group' },
        h('h5', { class: 'st-group-title mono-label' }, `${tab ? (g.items.length > 1 ? tab.plural : tab.label) : g.kind} · ${g.items.length}`),
        h('div', { class: 'st-minis' }, ...g.items.map((c) => mini(c))));
    }) : [h('p', { class: 'st-muted' }, t('studio.noChildren'))];
    out.push(section(t('studio.children'), h('div', { class: 'st-groups' }, ...body), item.children.length));
    return out;
  }

  function historyBlock() {
    return section(t('studio.history'), historyList(item, typeOf(deptById(item.deptId), item.kind), agentById));
  }

  function section(title, body, n = null, action = null) {
    const head = h('h4', { class: 'st-section-title' }, title, n != null ? h('span', { class: 'st-section-count' }, String(n)) : null);
    return h('section', { class: 'st-section' }, action ? h('div', { class: 'st-section-head' }, head, action) : head, body);
  }

  function treeBlock() {
    const tabList = kindTabs(studioDepts(store.get().office, item.floorId), { all: true });
    const root = tree?.tree;
    const { columns, rows } = treeRows(root, pipelineOrder());
    if (!rows.length) return section(t('studio.view.tree'), h('p', { class: 'st-muted' }, t('studio.tree.empty')));
    const label = (kind) => tabList.find((x) => x.kind === kind)?.plural || kind;
    const headRow = h('div', { class: 'st-tree-row st-tree-head', role: 'row' }, ...columns.map((k) => h('div', { class: 'st-tree-col mono-label', role: 'columnheader' }, label(k))));
    const body = rows.map((r) => h('div', { class: 'st-tree-row', role: 'row' }, ...columns.map((k) => {
      const cells = k === r.head.kind ? [r.head] : r.cells[k] || [];
      return h('div', { class: 'st-tree-cell', role: 'cell' },
        cells.length ? cells.map((c) => mini(c, { pendingOutline: true })) : h('span', { class: 'st-tree-missing' }, t('studio.tree.missing')));
    })));
    const grid = h('div', { class: 'st-tree', role: 'table', style: { '--cols': String(columns.length) } }, headRow, ...body);
    return section(t('studio.view.tree'), h('div', {}, h('p', { class: 'st-muted st-tree-legend' }, t('studio.tree.legend')), h('div', { class: 'st-tree-scroll' }, grid)));
  }

  function render() {
    if (!item) return;
    layer.setTitle?.(item.title);
    const floor = floors().find((f) => f.id === item.floorId);
    layer.setEyebrow?.(t('studio.eyebrow', { floor: (floor?.name || '').toUpperCase() }));
    const main = treeMode
      ? h('div', { class: 'st-item-tree' }, treeBlock())
      : h('div', { class: 'st-item-grid' },
        h('div', { class: 'st-item-main' }, filesBlock(), fieldsBlock()),
        h('aside', { class: 'st-item-side' }, ...relationsBlock(), historyBlock()));
    el.replaceChildren(crumbs(), header(), main);
  }

  async function load() {
    const mine = ++seq;
    try {
      const [detail, tr] = await Promise.all([
        ctx.api.get(`/api/studio/items/${enc(route.id)}`),
        ctx.api.get(`/api/studio/items/${enc(route.id)}/tree?depth=${treeMode ? 5 : 6}`).catch(() => null),
      ]);
      if (destroyed || mine !== seq) return;
      // A brief lives in the page of its pub (the newest one the person may open): no sheet of its
      // own, unless « /fiche ». Replace: « Retour » never comes back here to be sent on again.
      const pub = detail.kind === 'brief' && !route.tab ? newestPub(detail, { view: true }) : null;
      if (pub) {
        ctx.navigate(itemHash(pub.id), { replace: true });
        return;
      }
      if (detail.kind === 'pub' && (!route.tab || route.tab === 'v')) {
        pubView = createPubView(ctx, route, layer);
        el.className = 'st-view-host';
        el.replaceChildren(pubView.el);
        return;
      }
      // A scene, an image or a sequence is shown inside the page of its ad (« ← Pub 9 »), never on
      // a sheet of its own (« /fiche » keeps it). Replace: « Retour » never comes back here.
      if (!route.tab && STEP_IN_PUB.has(detail.kind)) {
        const owner = await ctx.api.get(`/api/studio/items/${enc(detail.id)}/pub`).catch(() => null);
        if (destroyed || mine !== seq) return;
        if (owner?.id) {
          ctx.navigate(routeHash({ name: 'studioItem', id: owner.id, tab: 'v', sub: detail.id }), { replace: true });
          return;
        }
      }
      item = detail;
      tree = tr;
      render();
    } catch (err) {
      if (destroyed || mine !== seq) return;
      el.replaceChildren(emptyState({
        icon: 'alert', title: err?.status === 404 ? t('studio.gone') : t('studio.loadError'), text: err?.status === 404 ? '' : err?.message || '',
        action: button(t('studio.back'), { variant: 'secondary', size: 'sm', onClick: () => backToList() }),
      }));
    }
  }

  /**
   * The most recent pub of the element's own floor among its children (null without any: a pub
   * another floor made from it stays with that floor); with `view`, only one of a service the person
   * may see (a pub of a hidden service would only show « introuvable »).
   * @param {object} el the element (its floorId, its children)
   * @param {{view?: boolean}} [opts]
   */
  function newestPub(el, { view = false } = {}) {
    const pubs = (el?.children || []).filter((c) => c.kind === 'pub' && (c.floorId || el.floorId) === el.floorId
      && (!view || ctx.can('view', { floorId: el.floorId, deptId: c.deptId })));
    return pubs.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))[0] || null;
  }

  /** May « Créer la pub » make the pub of this brief (operate, a studio on brief, no pub of its floor yet)? */
  function canMakePub() {
    if (item?.kind !== 'brief' || !canOperate() || newestPub(item)) return false;
    return adDepts([deptById(item.deptId)].filter(Boolean), () => true).length > 0;
  }

  /**
   * « Créer la pub » on a brief without one: the pub (first status, parent the brief), named like
   * the agents do (« Pub N · <brief> », N = the pubs of the floor + 1), then its page — in place of
   * the brief's sheet, which now leads there anyway (« Retour » goes back before the brief).
   */
  async function makePub(btn) {
    btn.disabled = true;
    try {
      let n = 0;
      try {
        n = (await ctx.api.get(ctx.api.withQuery(`/api/studio/floors/${enc(item.floorId)}/items`, { kind: 'pub', limit: 1 }))).total || 0;
      } catch { /* the number only names it */ }
      const title = `Pub ${n + 1} · ${item.title}`.slice(0, 200);
      const pub = await ctx.api.post(`/api/studio/depts/${enc(item.deptId)}/items`, { kind: 'pub', title, parents: [item.id] });
      ctx.toast.success(t('studio.brief.pubDone'), { timeout: 3000 });
      ctx.navigate(itemHash(pub.id), { replace: true });
    } catch (err) {
      ctx.toast.error(err);
    } finally {
      btn.disabled = false;
    }
  }

  function backToList() {
    const floorId = item?.floorId || store.get().ui.floorId;
    ctx.navigate(floorNumberOf(floors(), floorId) ? studioHash(floors(), floorId, item?.deptId || null) : '#/');
  }

  async function changeStatus(value, sel) {
    sel.disabled = true;
    try {
      item = await ctx.api.patch(`/api/studio/items/${enc(item.id)}`, { status: value });
      ctx.toast.success(t('studio.statusChanged', { label: infoOf(item).label }), { timeout: 2500 });
      render();
    } catch (err) {
      ctx.toast.error(err);
      sel.value = item.status;
    } finally {
      sel.disabled = false;
    }
  }

  async function regenerate(agent) {
    const reason = await ctx.overlay.prompt({
      title: t('studio.regen.title', { title: item.title }),
      label: t('studio.regen.label'),
      hint: agent ? t('studio.regen.hint', { agent: agent.name }) : t('studio.regen.hintLead'),
      multiline: true, maxLength: 2000, confirmLabel: t('studio.regen.confirm'),
    });
    if (!reason || destroyed) return;
    try {
      const out = await ctx.api.post(`/api/studio/items/${enc(item.id)}/regenerate`, { reason });
      ctx.toast.success(t('studio.regen.done'), { action: { label: t('studio.regen.open'), onClick: () => ctx.navigate(`#/task/${enc(out.taskId)}`) } });
      load();
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  async function remove() {
    const n = item.children.length;
    const ok = await ctx.overlay.confirm({
      title: t('studio.delete.title', { title: item.title }),
      message: `${t('studio.delete.message')}${n ? ` ${t('studio.delete.children', { n })}` : ''}`,
      confirmLabel: t('studio.delete'), danger: true,
    });
    if (!ok || destroyed) return;
    try {
      await ctx.api.del(`/api/studio/items/${enc(item.id)}`);
      ctx.toast.success(t('studio.deleted'));
      backToList();
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  let pending = null;
  function onEvent(ev) {
    if (pubView) { pubView.onEvent(ev); return; }
    if (!item || !ev) return;
    const related = ev.id === item.id || item.parents.some((p) => p.id === ev.id) || item.children.some((c) => c.id === ev.id)
      || (ev.item?.parents || []).includes(item.id);
    if (!related) return;
    if (editing && ev.action !== 'delete') return;
    if (ev.action === 'delete' && ev.id === item.id) {
      el.replaceChildren(emptyState({ icon: 'alert', title: t('studio.gone'), action: button(t('studio.back'), { variant: 'secondary', size: 'sm', onClick: () => backToList() }) }));
      item = null;
      return;
    }
    liveReload();
  }

  // A live update never rebuilds the page under a playing video or a running upload: it waits
  // (`waiting`) until the media pauses or ends (caught on the way down: they do not bubble); the
  // end of an upload reloads anyway.
  let waiting = false;
  function liveReload() {
    if (liveReloadWaits(el)) { waiting = true; return; }
    if (pending) return;
    pending = setTimeout(() => {
      timers.delete(pending);
      pending = null;
      if (liveReloadWaits(el)) { waiting = true; return; }
      waiting = false;
      load();
    }, RELOAD_DEBOUNCE_MS);
    timers.add(pending);
  }
  const resume = () => { if (waiting && !destroyed) liveReload(); };
  el.addEventListener('pause', resume, true);
  el.addEventListener('ended', resume, true);

  load();
  return {
    el,
    onEvent,
    /** The same ad with or without one of its elements: shown in place (true), else a new view. */
    tryShow(r) {
      if (!pubView || r.name !== 'studioItem' || r.id !== route.id || (r.tab && r.tab !== 'v')) return false;
      pubView.showSub(r.tab === 'v' ? r.sub || null : null);
      return true;
    },
    reload: () => (pubView ? pubView.reload() : load()),
    officeChanged: () => (pubView ? pubView.officeChanged() : render()),
    destroy() {
      destroyed = true;
      pubView?.destroy();
      for (const id of timers) clearTimeout(id);
      timers.clear();
    },
  };
}
